import { useState } from 'react';

import { AppIcon, AppIconProps } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  SxProps,
  Theme,
  List,
  ListItem,
  ListItemText,
  Chip,
  Tooltip,
  Checkbox,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';

export type AccordionDropdownSxOverrides = Readonly<{
  container?: SxProps<Theme>;
  header?: SxProps<Theme>;
  iconContainer?: SxProps<Theme>;
  title?: SxProps<Theme>;
  content?: SxProps<Theme>;
  listItem?: SxProps<Theme>;
  listItemText?: SxProps<Theme>;
}>;

type SubtitleItem = Readonly<{
  text: string;
  showSubtitleIcon?: boolean;
  subtitleIconColor?: AppIconProps['color'];
}>;

type AccordionDropdownProps = Readonly<{
  id?: string;
  title: string;
  iconName: AppIconProps['iconName'];
  subtitles?: (string | SubtitleItem)[];
  onSubtitleClick?: (subtitle: string) => void;
  badgeCount?: number;
  sxOverrides?: AccordionDropdownSxOverrides;
  iconColor?: AppIconProps['color'];
  iconDiameter?: AppIconProps['diameter'];
  tooltipText?: string;
  /** Controlled mode: whether accordion is expanded */
  expanded?: boolean;
  /** Controlled mode: callback when expansion state changes */
  onChange?: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  /** Enable checkbox selection mode for items */
  selectable?: boolean;
  /** Set of selected item texts (used when selectable is true) */
  selectedItems?: Set<string>;
  /** Callback when an item's selection state changes */
  onSelectionChange?: (item: string, selected: boolean) => void;
  /** Enable header/title selection (selects the whole category) */
  headerSelectable?: boolean;
  /** Whether the header is currently selected */
  isHeaderSelected?: boolean;
  /** Callback when header selection state changes */
  onHeaderSelectionChange?: (selected: boolean) => void;
}>;

export function AccordionDropdown({
  id,
  title,
  iconName,
  subtitles = [],
  onSubtitleClick,
  badgeCount = 0,
  sxOverrides,
  iconColor = 'Colors/Foreground/fg-secondary',
  iconDiameter = '24px',
  tooltipText,
  expanded,
  onChange,
  selectable = false,
  selectedItems,
  onSelectionChange,
  headerSelectable = false,
  isHeaderSelected = false,
  onHeaderSelectionChange,
}: AccordionDropdownProps) {
  const [showHeaderTooltip, setShowHeaderTooltip] = useState(false);
  const isControlled = expanded !== undefined;

  const handleSubtitleClick = (subtitle: string) => {
    if (onSubtitleClick) {
      onSubtitleClick(subtitle);
    }
  };

  const handleHeaderMouseEnter = () => {
    if (tooltipText) {
      setShowHeaderTooltip(true);
    }
  };

  const handleHeaderMouseLeave = () => {
    setShowHeaderTooltip(false);
  };

  const normalizeSubtitle = (
    subtitle: string | SubtitleItem,
  ): { text: string; showIcon: boolean; subtitleIconColor?: AppIconProps['color'] } => {
    if (typeof subtitle === 'string') {
      return { text: subtitle, showIcon: false };
    }

    return {
      text: subtitle.text,
      showIcon: subtitle.showSubtitleIcon || false,
      subtitleIconColor: subtitle.subtitleIconColor || 'Brand/700',
    };
  };

  return (
    <Accordion
      id={id}
      sx={[accordionSx, sxOverrides?.container] as SxProps<Theme>}
      {...(isControlled ? { expanded, onChange } : {})}
    >
      <Tooltip
        title={<ReactMarkdown>{tooltipText || ''}</ReactMarkdown>}
        open={showHeaderTooltip}
        placement="top"
      >
        <AccordionSummary
          expandIcon={
            <AppIcon
              iconName="ChevronDown"
              diameter={iconDiameter}
              color="Colors/Foreground/fg-secondary"
            />
          }
          sx={[headerSx, sxOverrides?.header] as SxProps<Theme>}
          onMouseEnter={handleHeaderMouseEnter}
          onMouseLeave={handleHeaderMouseLeave}
        >
          {headerSelectable && (
            <Checkbox
              checked={isHeaderSelected}
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onHeaderSelectionChange?.(!isHeaderSelected);
              }}
              sx={headerCheckboxSx}
            />
          )}
          <Box sx={headerContentSx}>
            <Box sx={[iconContainerSx, sxOverrides?.iconContainer] as SxProps<Theme>}>
              <AppIcon diameter={iconDiameter} iconName={iconName} color={iconColor} />
            </Box>
            <Typography
              variant="display-xs-semibold"
              color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-secondary']}
              sx={sxOverrides?.title}
            >
              {title}
            </Typography>
            {badgeCount > 0 && (
              <Chip
                variant="badge"
                label={badgeCount}
                size="sm"
                icon={
                  <AppIcon iconName="Circle" diameter="8px" color="Brand/700" fill="Brand/700" />
                }
                sx={chipSx}
                type="leading"
              />
            )}
          </Box>
        </AccordionSummary>
      </Tooltip>
      <AccordionDetails sx={[{}, sxOverrides?.content] as SxProps<Theme>}>
        {subtitles.length > 0 && (
          <List sx={listSx}>
            {subtitles.map((subtitle, index) => {
              const { text, showIcon, subtitleIconColor } = normalizeSubtitle(subtitle);

              const isSelected = selectable && selectedItems?.has(text);

              const handleItemClick = () => {
                if (selectable && onSelectionChange) {
                  onSelectionChange(text, !isSelected);
                } else {
                  handleSubtitleClick(text);
                }
              };

              return (
                <ListItem
                  key={index}
                  id={`accordion-item-${index}`}
                  sx={[listItemSx(!!onSubtitleClick), sxOverrides?.listItem] as SxProps<Theme>}
                  onClick={handleItemClick}
                >
                  {selectable && (
                    <Checkbox
                      checked={isSelected}
                      icon={<CheckBoxOutlineBlankIcon />}
                      checkedIcon={<CheckIcon />}
                      onClick={(e) => e.stopPropagation()}
                      onChange={handleItemClick}
                    />
                  )}
                  <Box sx={listItemTextWrapperSx}>
                    <ListItemText
                      primary={text}
                      sx={[listItemTextSx, sxOverrides?.listItemText] as SxProps<Theme>}
                    />
                    {showIcon && (
                      <AppIcon
                        iconName="Circle"
                        diameter="8px"
                        color={subtitleIconColor || 'Brand/700'}
                        fill={subtitleIconColor || 'Brand/700'}
                      />
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

const accordionSx: SxProps<Theme> = ({ backrs }) => ({
  boxShadow: 'none',
  margin: 0,
  '&.Mui-expanded': {
    boxShadow: backrs.shadows['shadow-xs-skeuomorphic'],
    borderRadius: backrs.radius['radius-lg'],
    margin: 0,
  },
  '&:before': {
    height: '0px',
  },
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  padding: backrs.spacing['spacing-lg'],
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  borderRadius: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-lg']}`,
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  minHeight: 'unset',
  height: '72px',
  '&.Mui-expanded': {
    minHeight: 'unset',
    height: '72px',
    borderRadius: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-none']} ${backrs.spacing['spacing-none']}`,
    border: 'none',
    borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  },
});

const headerContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  flex: '1 0 0',
});

const iconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  width: '48px',
  height: '48px',
  padding: '12px',
  justifyContent: 'center',
  alignContent: 'center',
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Gray/200']}`,
  background: backrs.colors['Colors/Base/White'],
});

const listSx: SxProps<Theme> = {
  padding: 0,
};

const listItemSx =
  (isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    padding: `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-md']}`,
    cursor: isClickable ? 'pointer' : 'default',
    borderRadius: backrs.radius['radius-sm'],
    ...(isClickable && {
      '&:hover': {
        transition: 'background 0.3s ease',
        background: backrs.colors['Indigo/100'],
      },
    }),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  });

const listItemTextWrapperSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'space-between',
  paddingRight: '5px',
};

const listItemTextSx: SxProps<Theme> = ({ backrs, typography }) => ({
  '& .MuiListItemText-primary': {
    color: backrs.colors['Colors/Text/text-secondary'],
    ...typography['text-sm-regular'],
    '& p': {
      margin: 0,
      ...typography['text-sm-regular'],
    },
  },
});

const chipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  '& .MuiChip-icon': {
    marginLeft: backrs.spacing['spacing-none'],
    marginRight: backrs.spacing['spacing-none'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiChip-label': {
    ...typography['text-sm-medium'],
  },
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
});

const headerCheckboxSx: SxProps<Theme> = ({ backrs }) => ({
  marginRight: backrs.spacing['spacing-sm'],
  padding: backrs.spacing['spacing-xs'],
});
